// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { QRCodeSVG } from 'qrcode.react'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA',
}
const serif = 'Georgia, serif'
const sans = 'Calibri, sans-serif'

const TABS = [
  { id: 'profile' },
  { id: 'account' },
  { id: 'notifications' },
  { id: 'language' },
  { id: 'about' },
]

const yearOptions = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 18 - i)

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: checked ? C.amber : C.border,
        position: 'relative', cursor: 'pointer', flexShrink: 0,
        transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute', top: 2,
        left: checked ? 22 : 2,
        width: 20, height: 20, borderRadius: '50%',
        background: '#FDFAF6', transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  )
}

function Alert({ type, msg }) {
  return (
    <div style={{
      borderRadius: 8, padding: '10px 14px', marginBottom: 16,
      fontSize: 13, fontFamily: sans,
      background: type === 'error' ? C.dangerBg : C.successBg,
      border: `1px solid ${type === 'error' ? C.danger : C.success}44`,
      color: type === 'error' ? C.danger : C.success,
    }}>
      {msg}
    </div>
  )
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, color: C.muted, letterSpacing: '2px',
      textTransform: 'uppercase', marginBottom: 12,
      fontFamily: sans, marginTop: 4,
    }}>
      {children}
    </div>
  )
}

function FieldLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, color: C.muted, letterSpacing: '1.5px',
      textTransform: 'uppercase', marginBottom: 5, fontFamily: sans,
    }}>
      {children}
    </div>
  )
}

function getInputStyle(focused, extra = {}) {
  return {
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: 8, padding: '11px 14px', fontSize: 16,
    fontFamily: serif, color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: 14, transition: 'border-color 0.2s',
    WebkitAppearance: 'none', ...extra,
  }
}

const selectExtraStyle = {
  cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239A7B68' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: 36,
}

function SaveBtn({ onClick, disabled, loading, label, loadingLabel, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        width: '100%',
        background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
        border: 'none', borderRadius: 10, padding: 14,
        color: '#FDFAF6', fontSize: 14, fontWeight: 700,
        letterSpacing: '2px', textTransform: 'uppercase',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        fontFamily: serif, opacity: disabled || loading ? 0.6 : 1,
      }}
    >
      {loading ? loadingLabel : label}
    </button>
  )
}

function checkExemptions(birthYear) {
  if (!birthYear) return []
  const age = new Date().getFullYear() - parseInt(birthYear)
  const list = []
  if (age >= 65) list.push({ name: 'Senior Homeowner Exemption', savings: '~$70–100/year', url: 'https://www.assessor.ca.gov' })
  if (age >= 62) list.push({ name: 'Senior Property Tax Postponement', savings: 'Defer taxes until sale', url: 'https://www.sco.ca.gov' })
  return list
}

export default function Settings({ session, onBack }) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('profile')
  const userId = session?.user?.id ?? null
  const userEmail = session?.user?.email ?? ''

  const [profile, setProfile] = useState({ first_name: '', last_name: '', phone: '', birth_year: '' })
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileAlert, setProfileAlert] = useState(null)

  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' })
  const [showPwd, setShowPwd] = useState({ current: false, newPass: false, confirm: false })
  const [pwSaving, setPwSaving] = useState(false)
  const [pwAlert, setPwAlert] = useState(null)

  const [notif, setNotif] = useState({ email_reminders_enabled: true, reminder_lead_days: 7, notification_email: '' })
  const [notifSaving, setNotifSaving] = useState(false)
  const [notifAlert, setNotifAlert] = useState(null)

  const [language, setLanguage] = useState('en')
  const [langSaving, setLangSaving] = useState(false)
  const [langAlert, setLangAlert] = useState(null)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteEmail, setDeleteEmail] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const [focus, setFocus] = useState('')

  const [homeId, setHomeId] = useState(null)
  const [shareToken, setShareToken] = useState(null)
  const [shareLoading, setShareLoading] = useState(true)
  const [shareCopied, setShareCopied] = useState(false)
  const [rotateConfirm, setRotateConfirm] = useState(false)
  const [rotating, setRotating] = useState(false)

  useEffect(() => {
    if (!userId) return
    supabase.from('profiles')
      .select('first_name,last_name,phone,birth_year,email_reminders_enabled,reminder_lead_days,notification_email,language_preference')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return
        setProfile({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          phone: data.phone || '',
          birth_year: data.birth_year ? String(data.birth_year) : '',
        })
        setNotif({
          email_reminders_enabled: data.email_reminders_enabled ?? true,
          reminder_lead_days: data.reminder_lead_days ?? 7,
          notification_email: data.notification_email || '',
        })
        if (data.language_preference) setLanguage(data.language_preference)
      })
  }, [userId])

  useEffect(() => {
    if (!userId) return
    supabase.from('home_members').select('home_id').eq('user_id', userId).limit(1).maybeSingle()
      .then(({ data: hm }) => {
        if (!hm?.home_id) { setShareLoading(false); return }
        setHomeId(hm.home_id)
        supabase.from('home_share_tokens').select('*').eq('home_id', hm.home_id).maybeSingle()
          .then(({ data: st }) => { setShareToken(st || null); setShareLoading(false) })
      })
  }, [userId])

  const rotateShareLink = async () => {
    if (!homeId || !shareToken) return
    setRotating(true)
    const arr = new Uint8Array(12)
    crypto.getRandomValues(arr)
    const newToken = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
    const { data } = await supabase.from('home_share_tokens')
      .update({ token: newToken, rotated_at: new Date().toISOString(), rotated_by: userId })
      .eq('id', shareToken.id)
      .select()
      .single()
    if (data) setShareToken(data)
    setRotating(false)
    setRotateConfirm(false)
  }

  const saveProfile = async () => {
    setProfileSaving(true)
    setProfileAlert(null)
    try {
      const { error } = await supabase.from('profiles').upsert({
        id: userId,
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        full_name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || null,
        phone: profile.phone || null,
        birth_year: profile.birth_year ? parseInt(profile.birth_year) : null,
      })
      if (error) throw error
      setProfileAlert({ type: 'success', msg: t('settings.profile_saved') })
    } catch (err) {
      setProfileAlert({ type: 'error', msg: err.message })
    }
    setProfileSaving(false)
  }

  const savePassword = async () => {
    if (!passwords.current) { setPwAlert({ type: 'error', msg: t('settings.err_current_password') }); return }
    if (!passwords.newPass || passwords.newPass.length < 8) { setPwAlert({ type: 'error', msg: t('settings.err_new_password_length') }); return }
    if (passwords.newPass !== passwords.confirm) { setPwAlert({ type: 'error', msg: t('settings.err_password_mismatch') }); return }
    setPwSaving(true)
    setPwAlert(null)
    const { error: signInErr } = await supabase.auth.signInWithPassword({ email: userEmail, password: passwords.current })
    if (signInErr) {
      setPwAlert({ type: 'error', msg: t('settings.err_wrong_current_password') })
      setPwSaving(false)
      return
    }
    const { error } = await supabase.auth.updateUser({ password: passwords.newPass })
    if (error) {
      setPwAlert({ type: 'error', msg: error.message })
    } else {
      setPwAlert({ type: 'success', msg: t('settings.password_updated') })
      setPasswords({ current: '', newPass: '', confirm: '' })
    }
    setPwSaving(false)
  }

  const saveNotif = async () => {
    setNotifSaving(true)
    setNotifAlert(null)
    try {
      const { error } = await supabase.from('profiles').update({
        email_reminders_enabled: notif.email_reminders_enabled,
        reminder_lead_days: notif.reminder_lead_days,
        notification_email: notif.notification_email || null,
      }).eq('id', userId)
      if (error) throw error
      setNotifAlert({ type: 'success', msg: t('settings.notifications_saved') })
    } catch (err) {
      setNotifAlert({ type: 'error', msg: err.message })
    }
    setNotifSaving(false)
  }

  const saveLanguage = async () => {
    setLangSaving(true)
    setLangAlert(null)
    try {
      const { error } = await supabase.from('profiles').update({
        language_preference: language,
      }).eq('id', userId)
      if (error) throw error
      await i18n.changeLanguage(language)
      setLangAlert({ type: 'success', msg: t('settings.language_saved') })
    } catch (err) {
      setLangAlert({ type: 'error', msg: err.message })
    }
    setLangSaving(false)
  }

  const handleDeleteAccount = async () => {
    if (deleteEmail.trim().toLowerCase() !== userEmail.toLowerCase()) {
      setDeleteError(t('settings.err_email_mismatch'))
      return
    }
    setDeleting(true)
    setDeleteError('')
    try {
      const { data: { session: sess } } = await supabase.auth.getSession()
      const { error } = await supabase.functions.invoke('delete-account', {
        headers: { Authorization: `Bearer ${sess?.access_token}` },
      })
      if (error) throw error
      await supabase.auth.signOut()
      window.location.href = '/'
    } catch (err) {
      setDeleteError(err.message || t('settings.err_delete_failed'))
      setDeleting(false)
    }
  }

  const initials = (
    (profile.first_name || '').charAt(0).toUpperCase() +
    (profile.last_name || '').charAt(0).toUpperCase()
  ) || userEmail.charAt(0).toUpperCase()

  const exemptions = checkExemptions(profile.birth_year)

  if (!userId) return null

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: sans }}>

      {/* Header */}
      <div style={{
        background: C.card, borderBottom: `1px solid ${C.border}`,
        padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button
          data-testid="btn-settings-back"
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.amber, fontSize: 14, fontFamily: sans,
            padding: 0, display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          ← Back
        </button>
        <h1 style={{ fontFamily: serif, fontSize: 20, color: C.brown, margin: 0, flex: 1 }}>
          {t('settings.title')}
        </h1>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
          color: '#FDFAF6', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, fontFamily: sans,
        }}>
          {initials}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        background: C.card, borderBottom: `1px solid ${C.border}`,
        display: 'flex', overflowX: 'auto', padding: '0 20px',
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            data-testid={`settings-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '13px 14px', fontSize: 13, fontFamily: sans,
              color: activeTab === tab.id ? C.amber : C.muted,
              borderBottom: activeTab === tab.id ? `2px solid ${C.amber}` : '2px solid transparent',
              fontWeight: activeTab === tab.id ? 600 : 400,
              whiteSpace: 'nowrap', transition: 'color 0.15s',
              marginBottom: -1,
            }}
          >
            {t(`settings.tab_${tab.id}`)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>

        {/* ── PROFILE ── */}
        {activeTab === 'profile' && (
          <>
            <SectionTitle>{t('settings.profile_section')}</SectionTitle>
            {profileAlert && <Alert {...profileAlert} />}

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel>{t('settings.first_name_label')}</FieldLabel>
                <input
                  style={getInputStyle(focus === 'first_name')}
                  value={profile.first_name}
                  onChange={e => { setProfile(p => ({ ...p, first_name: e.target.value })); setProfileAlert(null) }}
                  onFocus={() => setFocus('first_name')}
                  onBlur={() => setFocus('')}
                  placeholder="First name"
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel>{t('settings.last_name_label')}</FieldLabel>
                <input
                  style={getInputStyle(focus === 'last_name')}
                  value={profile.last_name}
                  onChange={e => { setProfile(p => ({ ...p, last_name: e.target.value })); setProfileAlert(null) }}
                  onFocus={() => setFocus('last_name')}
                  onBlur={() => setFocus('')}
                  placeholder="Last name"
                  autoComplete="off"
                />
              </div>
            </div>

            <FieldLabel>{t('settings.phone_label')}</FieldLabel>
            <input
              style={getInputStyle(focus === 'phone')}
              value={profile.phone}
              onChange={e => { setProfile(p => ({ ...p, phone: e.target.value })); setProfileAlert(null) }}
              onFocus={() => setFocus('phone')}
              onBlur={() => setFocus('')}
              placeholder="Phone number"
              type="tel"
            />

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
              <FieldLabel>{t('settings.birth_year_label')}</FieldLabel>
              <span style={{ fontSize: 11, color: C.muted, fontFamily: sans }}>
                {t('settings.birth_year_hint')}
              </span>
            </div>
            <select
              style={getInputStyle(focus === 'birth_year', selectExtraStyle)}
              value={profile.birth_year}
              onChange={e => { setProfile(p => ({ ...p, birth_year: e.target.value })); setProfileAlert(null) }}
              onFocus={() => setFocus('birth_year')}
              onBlur={() => setFocus('')}
            >
              <option value="">Prefer not to say</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>

            {exemptions.length > 0 && (
              <div style={{
                background: '#FFF8E7', border: `1px solid ${C.amber}`,
                borderRadius: 8, padding: 12, marginBottom: 14,
              }}>
                <div style={{ fontSize: 13, color: C.amber, fontWeight: 600, fontFamily: sans, marginBottom: 6 }}>
                  You may qualify for property tax exemptions
                </div>
                {exemptions.map(e => (
                  <div key={e.name} style={{ fontSize: 12, color: C.brown, fontFamily: sans, marginBottom: 4 }}>
                    • {e.name} — {e.savings}{' '}
                    <a href={e.url} target="_blank" rel="noopener noreferrer" style={{ color: C.amber }}>Learn more →</a>
                  </div>
                ))}
              </div>
            )}

            <SaveBtn
              testId="btn-save-profile"
              onClick={saveProfile}
              loading={profileSaving}
              label={t('settings.save_profile')}
              loadingLabel={t('settings.saving')}
            />

            {/* ── SHARE LINK ── */}
            <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 28, paddingTop: 24 }}>
              <SectionTitle>{t('settings.shareLinkTitle')}</SectionTitle>
              <div style={{ fontSize: 13, color: C.muted, fontFamily: sans, fontStyle: 'italic', lineHeight: 1.6, marginBottom: 20 }}>
                {t('settings.shareLinkSubtitle')}
              </div>

              {shareLoading ? (
                <div style={{ fontSize: 13, color: C.muted, fontFamily: sans }}>Loading…</div>
              ) : !shareToken ? (
                <div style={{ fontSize: 13, color: C.muted, fontFamily: sans, fontStyle: 'italic' }}>
                  {t('settings.shareLinkPending')}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                    <QRCodeSVG
                      value={`https://casavio-app.vercel.app/connect/${shareToken.token}`}
                      size={160}
                      level="M"
                      fgColor={C.brown}
                      bgColor="#FDFAF6"
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1, fontSize: 12, color: C.muted, fontFamily: sans, wordBreak: 'break-all' }}>
                      casavio-app.vercel.app/connect/{shareToken.token}
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`https://casavio-app.vercel.app/connect/${shareToken.token}`)
                        setShareCopied(true)
                        setTimeout(() => setShareCopied(false), 2000)
                      }}
                      style={{ background: shareCopied ? C.success : C.amber, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontFamily: sans, cursor: 'pointer', flexShrink: 0, fontWeight: 600 }}
                    >
                      {shareCopied ? t('settings.copied') : t('settings.copyLink')}
                    </button>
                  </div>

                  <div style={{ fontSize: 13, color: C.muted, fontFamily: sans, marginBottom: 16 }}>
                    {t('settings.shareLinkScanCount', { count: shareToken.scan_count ?? 0 })}
                  </div>

                  {!rotateConfirm ? (
                    <button
                      onClick={() => setRotateConfirm(true)}
                      style={{ background: 'none', border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: '8px 16px', fontSize: 12, fontFamily: sans, cursor: 'pointer', fontWeight: 600 }}
                    >
                      {t('settings.rotateLink')}
                    </button>
                  ) : (
                    <div style={{ background: '#FDECEA', border: `1px solid ${C.danger}44`, borderRadius: 10, padding: '14px 16px' }}>
                      <div style={{ fontSize: 13, color: C.danger, fontWeight: 700, fontFamily: sans, marginBottom: 6 }}>
                        {t('settings.rotateLinkConfirmTitle')}
                      </div>
                      <div style={{ fontSize: 12, color: C.brown, fontFamily: sans, lineHeight: 1.6, marginBottom: 14 }}>
                        {t('settings.rotateLinkConfirmBody')}
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          onClick={rotateShareLink}
                          disabled={rotating}
                          style={{ background: C.danger, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontFamily: sans, cursor: rotating ? 'default' : 'pointer', fontWeight: 600, opacity: rotating ? 0.7 : 1 }}
                        >
                          {rotating ? t('settings.saving') : t('settings.rotateLink')}
                        </button>
                        <button
                          onClick={() => setRotateConfirm(false)}
                          style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 8, padding: '8px 16px', fontSize: 12, fontFamily: sans, cursor: 'pointer' }}
                        >
                          {t('settings.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* ── ACCOUNT ── */}
        {activeTab === 'account' && (
          <>
            <SectionTitle>{t('settings.account_section')}</SectionTitle>
            <FieldLabel>{t('settings.email_label')}</FieldLabel>
            <input
              style={getInputStyle(false, { opacity: 0.65, cursor: 'not-allowed', marginBottom: 4 })}
              value={userEmail}
              readOnly
              disabled
            />
            <div style={{ fontSize: 11, color: C.muted, fontFamily: sans, marginBottom: 24 }}>
              {t('settings.email_readonly_hint')}
            </div>

            <div style={{ borderTop: `1px solid ${C.border}`, marginBottom: 20 }} />
            <SectionTitle>{t('settings.change_password_section')}</SectionTitle>
            {pwAlert && <Alert {...pwAlert} />}

            <FieldLabel>{t('settings.current_password_label')}</FieldLabel>
            <div style={{ position: 'relative' }}>
              <input
                style={getInputStyle(focus === 'pw_current', { paddingRight: 40 })}
                type={showPwd.current ? 'text' : 'password'}
                value={passwords.current}
                onChange={e => { setPasswords(p => ({ ...p, current: e.target.value })); setPwAlert(null) }}
                onFocus={() => setFocus('pw_current')}
                onBlur={() => setFocus('')}
                placeholder="Your current password"
                autoComplete="current-password"
              />
              <button
                onClick={() => setShowPwd(p => ({ ...p, current: !p.current }))}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, marginTop: -7 }}
              >
                {showPwd.current ? '🙈' : '👁️'}
              </button>
            </div>

            <FieldLabel>{t('settings.new_password_label')}</FieldLabel>
            <div style={{ position: 'relative' }}>
              <input
                style={getInputStyle(focus === 'pw_new', { paddingRight: 40 })}
                type={showPwd.newPass ? 'text' : 'password'}
                value={passwords.newPass}
                onChange={e => { setPasswords(p => ({ ...p, newPass: e.target.value })); setPwAlert(null) }}
                onFocus={() => setFocus('pw_new')}
                onBlur={() => setFocus('')}
                placeholder="New password (8+ characters)"
                autoComplete="new-password"
              />
              <button
                onClick={() => setShowPwd(p => ({ ...p, newPass: !p.newPass }))}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, marginTop: -7 }}
              >
                {showPwd.newPass ? '🙈' : '👁️'}
              </button>
            </div>

            <FieldLabel>{t('settings.confirm_password_label')}</FieldLabel>
            <div style={{ position: 'relative' }}>
              <input
                style={getInputStyle(focus === 'pw_confirm', {
                  paddingRight: 40,
                  borderColor: passwords.confirm && passwords.newPass !== passwords.confirm
                    ? C.danger : focus === 'pw_confirm' ? C.amber : C.border,
                })}
                type={showPwd.confirm ? 'text' : 'password'}
                value={passwords.confirm}
                onChange={e => { setPasswords(p => ({ ...p, confirm: e.target.value })); setPwAlert(null) }}
                onFocus={() => setFocus('pw_confirm')}
                onBlur={() => setFocus('')}
                placeholder="Repeat new password"
                autoComplete="new-password"
              />
              <button
                onClick={() => setShowPwd(p => ({ ...p, confirm: !p.confirm }))}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, marginTop: -7 }}
              >
                {showPwd.confirm ? '🙈' : '👁️'}
              </button>
            </div>
            {passwords.confirm && passwords.newPass !== passwords.confirm && (
              <div style={{ fontSize: 11, color: C.danger, marginTop: -10, marginBottom: 14, fontFamily: sans }}>
                {t('settings.passwords_no_match')}
              </div>
            )}
            {passwords.confirm && passwords.newPass === passwords.confirm && passwords.newPass && (
              <div style={{ fontSize: 11, color: C.success, marginTop: -10, marginBottom: 14, fontFamily: sans }}>
                {t('settings.passwords_match')}
              </div>
            )}

            <SaveBtn
              testId="btn-update-password"
              onClick={savePassword}
              loading={pwSaving}
              label={t('settings.update_password')}
              loadingLabel={t('settings.updating')}
            />

            <div style={{ borderTop: `1px solid ${C.border}`, margin: '28px 0 20px' }} />
            <SectionTitle>{t('settings.danger_zone')}</SectionTitle>
            <button
              data-testid="btn-delete-account"
              onClick={() => setShowDeleteModal(true)}
              style={{
                width: '100%', background: 'transparent',
                border: `1px solid ${C.danger}44`, borderRadius: 10, padding: 12,
                color: C.danger, fontSize: 13, letterSpacing: '1px',
                textTransform: 'uppercase', cursor: 'pointer', fontFamily: serif,
              }}
            >
              {t('settings.delete_account')}
            </button>
          </>
        )}

        {/* ── NOTIFICATIONS ── */}
        {activeTab === 'notifications' && (
          <>
            <SectionTitle>{t('settings.notif_section')}</SectionTitle>
            {notifAlert && <Alert {...notifAlert} />}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.brown, fontFamily: sans }}>
                {t('settings.notif_enabled_label')}
              </div>
              <Toggle
                checked={notif.email_reminders_enabled}
                onChange={v => setNotif(n => ({ ...n, email_reminders_enabled: v }))}
              />
            </div>

            {notif.email_reminders_enabled && (
              <>
                <FieldLabel>{t('settings.notif_lead_label')}</FieldLabel>
                <select
                  style={getInputStyle(focus === 'notif_lead', selectExtraStyle)}
                  value={notif.reminder_lead_days}
                  onChange={e => setNotif(n => ({ ...n, reminder_lead_days: parseInt(e.target.value) }))}
                  onFocus={() => setFocus('notif_lead')}
                  onBlur={() => setFocus('')}
                >
                  <option value={1}>1 day before</option>
                  <option value={3}>3 days before</option>
                  <option value={7}>7 days before (recommended)</option>
                  <option value={14}>14 days before</option>
                  <option value={30}>30 days before</option>
                </select>

                <FieldLabel>{t('settings.notif_email_label')}</FieldLabel>
                <input
                  style={getInputStyle(focus === 'notif_email')}
                  value={notif.notification_email}
                  onChange={e => setNotif(n => ({ ...n, notification_email: e.target.value }))}
                  onFocus={() => setFocus('notif_email')}
                  onBlur={() => setFocus('')}
                  placeholder={t('settings.notif_email_placeholder')}
                  type="email"
                />
              </>
            )}

            <SaveBtn
              testId="btn-save-notifications"
              onClick={saveNotif}
              loading={notifSaving}
              label={t('settings.save_notifications')}
              loadingLabel={t('settings.saving')}
            />
          </>
        )}

        {/* ── LANGUAGE ── */}
        {activeTab === 'language' && (
          <>
            <SectionTitle>{t('settings.language_section')}</SectionTitle>
            {langAlert && <Alert {...langAlert} />}
            <FieldLabel>{t('settings.language_label')}</FieldLabel>
            <select
              data-testid="settings-language-select"
              style={getInputStyle(focus === 'language', selectExtraStyle)}
              value={language}
              onChange={e => { setLanguage(e.target.value); setLangAlert(null) }}
              onFocus={() => setFocus('language')}
              onBlur={() => setFocus('')}
            >
              <option value="en">{t('settings.language_en')}</option>
              <option value="es">{t('settings.language_es')}</option>
            </select>
            <SaveBtn
              testId="btn-save-language"
              onClick={saveLanguage}
              loading={langSaving}
              label={t('settings.save_language')}
              loadingLabel={t('settings.saving')}
            />
          </>
        )}

        {/* ── ABOUT ── */}
        {activeTab === 'about' && (
          <>
            <SectionTitle>{t('settings.about_version')}</SectionTitle>
            <div style={{ fontSize: 14, color: C.brown, fontFamily: sans, marginBottom: 8 }}>
              Casavio v0.1.0
            </div>
            <div style={{ fontSize: 13, color: C.muted, fontFamily: sans, marginBottom: 28 }}>
              {t('settings.about_built')}
            </div>

            {[
              { label: t('settings.about_privacy'), href: '/privacy' },
              { label: t('settings.about_terms'), href: '/terms' },
              { label: t('settings.about_nda'), href: '/nda' },
            ].map(link => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 0', borderBottom: `1px solid ${C.border}`,
                  fontSize: 14, color: C.brown, fontFamily: sans,
                  textDecoration: 'none',
                }}
              >
                {link.label}
                <span style={{ color: C.muted, fontSize: 12 }}>→</span>
              </a>
            ))}

            <div style={{ padding: '14px 0', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 14, color: C.brown, fontFamily: sans }}>
                {t('settings.about_contact')}
              </div>
              <a
                href="mailto:kennethkrummes@gmail.com"
                style={{ fontSize: 13, color: C.amber, fontFamily: sans }}
              >
                {t('settings.about_contact_email')}
              </a>
            </div>
          </>
        )}
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 300, padding: 16, fontFamily: sans,
          }}
          onClick={() => !deleting && setShowDeleteModal(false)}
        >
          <div
            style={{
              background: C.card, borderRadius: 16, padding: 28,
              maxWidth: 440, width: '100%',
              boxShadow: '0 12px 40px rgba(44,29,20,0.2)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ fontFamily: serif, fontSize: 20, color: C.brown, margin: '0 0 12px' }}>
              {t('settings.delete_modal_title')}
            </h2>
            <p style={{ fontSize: 14, color: C.muted, margin: '0 0 20px', lineHeight: 1.5 }}>
              {t('settings.delete_modal_body')}
            </p>
            <FieldLabel>{t('settings.delete_confirm_label')}</FieldLabel>
            <input
              data-testid="input-delete-confirm"
              style={getInputStyle(focus === 'delete_email', { borderColor: C.danger + '88' })}
              value={deleteEmail}
              onChange={e => { setDeleteEmail(e.target.value); setDeleteError('') }}
              onFocus={() => setFocus('delete_email')}
              onBlur={() => setFocus('')}
              placeholder={userEmail}
              type="email"
              disabled={deleting}
            />
            {deleteError && <Alert type="error" msg={deleteError} />}
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button
                onClick={() => !deleting && setShowDeleteModal(false)}
                style={{
                  flex: 1, background: 'transparent', border: `1px solid ${C.border}`,
                  borderRadius: 8, padding: '11px 16px', fontSize: 14,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: sans, color: C.muted,
                }}
                disabled={deleting}
              >
                {t('settings.cancel')}
              </button>
              <button
                data-testid="btn-confirm-delete"
                onClick={handleDeleteAccount}
                style={{
                  flex: 2, background: C.danger, border: 'none',
                  borderRadius: 8, padding: '11px 16px', fontSize: 14,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  color: '#FDFAF6', fontWeight: 600, fontFamily: sans,
                  opacity: deleting ? 0.7 : 1,
                }}
                disabled={deleting}
              >
                {deleting ? t('settings.deleting') : t('settings.delete_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
